import * as Sentry from '@sentry/react';
import { deviceTypes } from '@airtm/utils/dist/constants';
import fetch from 'unfetch';
import buildNotification from 'utils/buildNotification';
import i18n from 'i18next';
import { ApolloClient, InMemoryCache, ApolloLink, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { persistCache, SessionStorageWrapper } from 'apollo3-cache-persist';
import { SentryLink } from 'apollo-link-sentry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

import { LOCAL_STORAGE, LOCAL_STORAGE_WHITELIST } from '@constants';
import JwtManager from 'lib/JwtManager';
import { RefreshJwt as REFRESH_JWT_MUTATION } from 'graphqlDocuments/mutations';
import { resolvers } from './clientSchema';
import {
  initialData as initialNetworkStatus,
  writeNetworkStatus,
} from './clientSchema/NetworkStatus';
import {
  initialData as initialNotifications,
  readNotifications,
  writeNotifications,
} from './clientSchema/Notification';
import introspectionResult from './introspectionResult';

import typeDefs from './local-schema.graphql';

const mergeAirnerArrayField = (existing, incoming) => {
  if (incoming || existing === undefined) {
    return incoming;
  }

  return existing;
};

const cache = new InMemoryCache({
  // https://www.graphql-code-generator.com/docs/plugins/fragment-matcher
  possibleTypes: introspectionResult.possibleTypes,
  typePolicies: {
    Catalogs__State: {
      keyFields: ['countryId', 'id'],
    },
    Crypto__Wallet: {
      keyFields: ['walletId'],
    },
    Numbers__Permission: {
      keyFields: ['userId', 'paymentMethodId'],
    },
    SecurityHub__Tier: {
      keyFields: ['userId'],
    },
    PaymentMethods__DirectFees: {
      keyFields: ['userId', 'paymentMethodId'],
    },
    Saasquatch__Promotion: {
      keyFields: ['promotionId'],
    },
    PaymentMethods__ExchangeAmounts: {
      keyFields: ['type', 'categoryId'],
    },
    PaymentMethods__BridgeRetailUserSignupStatus: {
      keyFields: ['bridgeCustomerId'],
    },
    YBB__User: {
      keyFields: ['userId'],
    },
    // By default, in the cache, the field data is completely replaced by the incoming one.
    // Apollo throws a warning if the merge function is not explicitily set for arrays and objects.
    PaymentMethods__SuggestedCategoriesResponse: {
      // Merge types of suggested payment methods (byTag, byCountry, recent)
      merge: true,
    },
    PaymentMethods__Category: {
      merge: true,
    },
    SecurityHub__User__Volumes: {
      merge: true,
    },
    Airners__AirnerUser: {
      fields: {
        workExperience: {
          merge: mergeAirnerArrayField,
        },
        languages: {
          merge: mergeAirnerArrayField,
        },
      },
    },
    Query: {
      fields: {
        availableOperations: {
          // Only keep incoming available operations
          merge: false,
        },
        myPaginatedOperations: {
          merge: true,
        },
        unifiedRate: {
          keyArgs: ({ fromAmountInput, ...args }) => Object.keys(args),
        },
        job: {
          read(existing, { args: { id }, toReference }) {
            return existing ?? toReference({ __typename: 'Airners__Job', id });
          },
        },
        yBBUser: {
          read(existing, { readField }) {
            return existing ?? readField('optInToYBB');
          },
        },
      },
    },
  },
});

const isDevEnv = process.env.NODE_ENV === 'development';

const OPERATIONS_WITH_HEADERS = ['UploadAttachment'];

const PERSISTOR_MAX_SIZE = 2 * 1024 * 1024; // 2 MB
const PERSISTOR_STORAGE = new SessionStorageWrapper(window.sessionStorage);

const getSizeInBytes = (data) => {
  try {
    return data.length; // UTF-8
  } catch {
    return null;
  }
};

persistCache({
  cache,
  maxSize: PERSISTOR_MAX_SIZE,
  storage: PERSISTOR_STORAGE,
  persistenceMapper: async (data) => {
    const dataSizeInBytes = getSizeInBytes(data);

    const notSupported = dataSizeInBytes === null || dataSizeInBytes > PERSISTOR_MAX_SIZE;

    if (notSupported) {
      const prevData = PERSISTOR_STORAGE.getItem('apollo-cache-persist');

      return prevData;
    }

    return data;
  },
});

const retryLink = new RetryLink({
  attempts: {
    max: Infinity,
    retryIf: (error, operation) => {
      const isQuery = operation.query?.definitions?.[0]?.operation === 'query';
      if (
        isQuery &&
        // retry gateway errors
        ([502, 504].includes(error.statusCode) ||
          // or changed ip auth error
          error.extensions?.reason === 'CHANGED_IP')
      ) {
        writeNetworkStatus({
          __typename: 'Local__NetworkStatus',
          online: true,
          retrying: true,
        });
        return true;
      }
      return false;
    },
  },
  delay: {
    initial: 300,
    jitter: true,
    max: 30000,
  },
});

const refererLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    'X-Referer': window.location.href.split('?')[0],
  },
}));

const appsflyerLink = setContext((_, { headers }) => {
  const afUserId = globalThis.AF_SDK?.PLUGINS?.PBA?.getAfWebUserId();

  return {
    headers: {
      ...headers,
      ...(afUserId && { 'X-AppsFlyer-Web-Id': afUserId }),
    },
  };
});

const getDeviceType = () => {
  if (window.RENDER_ON_APP) {
    return window.DEVICE_TYPE || deviceTypes.ANDROID;
  }

  const isAndroid = /android/i.test(navigator.userAgent);
  // iPhone and iPad including iPadOS 13+ regardless of desktop mode settings
  // https://stackoverflow.com/a/9039885
  const isIOS =
    /iPhone|iPad/.test(navigator.userAgent) ||
    (/Mac/.test(navigator.userAgent) && 'ontouchend' in document);

  if (isAndroid) {
    return deviceTypes.ANDROID_WEB;
  }

  if (isIOS) {
    return deviceTypes.IOS_WEB;
  }

  return deviceTypes.DESKTOP;
};

const deviceTypeLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    'X-Device-Type': getDeviceType(),
  },
}));

const webappNameLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    'X-Webapp-Name': __APP_NAME__,
  },
}));

const langLink = setContext((_, { headers }) => {
  const { language } = i18n;

  return {
    headers: {
      ...headers,
      'Accept-Language': language,
    },
  };
});

const authLink = setContext((_, { headers }) => {
  const { jwt } = JwtManager;
  const kountSessionid = localStorage.getItem(LOCAL_STORAGE.KOUNT_SESSIONID);
  return {
    headers: {
      ...headers,
      'X-Kount-Session-Id': kountSessionid,
      authorization: jwt ? `Bearer ${jwt}` : '',
    },
  };
});

const networkStatusLink = new ApolloLink((operation, forward) =>
  forward(operation).map((response) => {
    writeNetworkStatus({
      __typename: 'Local__NetworkStatus',
      online: true,
      retrying: false,
    });

    return response;
  }),
);

const errorLink = onError(({ graphQLErrors, networkError, operation, response }) => {
  const headers = OPERATIONS_WITH_HEADERS.includes(operation.operationName)
    ? operation.getContext()?.response?.headers
    : null;
  if (graphQLErrors) {
    const notifications = readNotifications();
    graphQLErrors.forEach((error) => {
      if (headers) {
        Object.assign(error.extensions, { headers });
      }

      switch (error.extensions.code) {
        case 'ACCEPT_LIMIT_REACHED_ERROR':
          writeNotifications([
            ...notifications,
            buildNotification({
              autoClose: true,
              errorCode: error.extensions.errorNumber,
              message: i18n.t('ERRORS:ACCEPT_LIMIT_REACHED_MESSAGE'),
              title: i18n.t('ERRORS:ACCEPT_LIMIT_REACHED_TITLE'),
              type: 'informative',
            }),
          ]);
          break;
        case 'JWT_SESSION_ERROR':
          JwtManager.destroyJwt();
          // Since the user is logged out, there's no need to pass the error up in the link chain
          response.errors = undefined;
          break;
        case 'UNAUTHENTICATED':
          JwtManager.destroyJwt();
          break;
        case 'MAX_UNCONFIRMED_ACCEPTED_SELLS_REACHED':
          writeNotifications([
            ...notifications,
            buildNotification({
              autoClose: true,
              errorCode: error.extensions.errorNumber,
              message: i18n.t('ERRORS:MAX_UNCONFIRMED_ACCEPTED_SELLS_REACHED_MESSAGE'),
              title: i18n.t('ERRORS:MAX_UNCONFIRMED_ACCEPTED_SELLS_REACHED_TITLE'),
              type: 'informative',
            }),
          ]);
          break;
        case 'RATE_LIMIT_ERROR': {
          const { id } = error.extensions;
          writeNotifications([
            ...notifications,
            buildNotification({
              autoClose: true,
              errorCode: error.extensions.errorNumber,
              message: i18n.t([
                `ERRORS:RATE_LIMIT_ERROR_MESSAGE_${id}`,
                'ERRORS:RATE_LIMIT_ERROR_MESSAGE',
              ]),
              title: i18n.t([
                `ERRORS:RATE_LIMIT_ERROR_TITLE_${id}`,
                'ERRORS:RATE_LIMIT_ERROR_TITLE',
              ]),
              type: 'informative',
            }),
          ]);
          break;
        }
        default:
          break;
      }
    });
  }

  if (networkError) {
    if (headers) {
      Object.assign(networkError, { headers });
    }
    Sentry.addBreadcrumb({
      category: 'networkError',
      data: {
        networkError: JSON.stringify(networkError, null, 2),
        operation: operation.operationName,
        query: operation.query.loc?.source.body,
        variables: JSON.stringify(operation.variables, null, 2),
      },
      level: 'error',
      message: `[Network error]: ${networkError.message || 'An unexpected error occurred'}`,
    });

    if (!networkError.statusCode) {
      // an undefined statusCode means the server could not be reached.
      // Most likely, the user is offline.
      writeNetworkStatus({
        __typename: 'Local__NetworkStatus',
        online: false,
        retrying: false,
      });
    }
  }
});

// https://www.npmjs.com/package/apollo-link-sentry/
const sentryLink = new SentryLink({
  setTransaction: true,
  setFingerprint: true,
  attachBreadcrumbs: {
    includeQuery: false,
    includeVariables: true,
    includeFetchResult: true,
    includeError: true,
    includeCache: false,
    includeContext: false,
    transform: (breadcrumb, operation) => {
      // remove sensible or redundant data response
      const isSensibleOperation = [
        'CreateChatMessage',
        'getChatMessages',
        'getChatNotifications',
        'Login',
        'ChangePassword',
        'ConfirmPasswordReset',
        'SignUpPartial',
      ].includes(operation.operationName);

      if (isSensibleOperation) {
        return {
          ...breadcrumb,
          data: {
            ...breadcrumb.data,
            fetchResult: undefined,
            variables: undefined,
          },
        };
      }

      return breadcrumb;
    },
  },
});

const httpLink = createUploadLink({
  credentials: 'include',
  fetch,
  uri: process.env.CORSOLA_URL,
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: process.env.CORSOLA_WS_URL,
    connectionParams: () => {
      const { jwt } = JwtManager;

      return {
        authorization: jwt ? `Bearer ${jwt}` : '',
        'X-Device-Type': getDeviceType(),
      };
    },
    shouldRetry: () => true,
  }),
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink,
);

export const link = ApolloLink.from([
  sentryLink,
  retryLink,
  errorLink,
  authLink,
  langLink,
  deviceTypeLink,
  webappNameLink,
  refererLink,
  appsflyerLink,
  networkStatusLink,
  splitLink,
]);

const client = new ApolloClient({
  name: 'web',
  cache,
  defaultOptions: {
    query: {
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
    watchQuery: {
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    },
  },
  link,
  resolvers,
  typeDefs,
  connectToDevTools: isDevEnv,
});

cache.policies.addTypePolicies({
  Auth__User: {
    fields: {
      status: {
        merge(existing, incoming) {
          if (incoming && JwtManager.currentUser?.status !== incoming) {
            client
              .mutate({
                mutation: REFRESH_JWT_MUTATION,
              })
              .then((result) => {
                const newJwt = result.data?.refreshJwt?.jwt;
                if (newJwt) {
                  JwtManager.jwt = newJwt;
                }
              });
          }

          return incoming;
        },
      },
    },
  },
});

const initializeData = () => {
  writeNetworkStatus(initialNetworkStatus);
  writeNotifications(initialNotifications);
};

initializeData();

// on reset apollo cache, we clear local and session storages
client.onClearStore(() => {
  sessionStorage.clear();
  Object.keys(localStorage).forEach((key) => {
    if (!LOCAL_STORAGE_WHITELIST.includes(key)) {
      localStorage.removeItem(key);
    }
  });
  initializeData();
});

// if jwt is expired, reset apollo cache
const onJwtUpdated = (jwt) => {
  if (!jwt) client.clearStore();
};

JwtManager.addJwtListener(onJwtUpdated);

export default client;
